import React from "react";
import { Comment } from '@roc-digital/types';
import { getConfig } from '@roc-digital/mxm-base/config';
import { debounce, JsonFetch } from "@roc-digital/mxm-base/logic";
import { useDebouncedState, useMethod } from "@roc-digital/ui-lib";

interface UseAdminComments {
  loadMore();
  delete(comment: Comment);
  edit(comment: Comment, body: string);
  setFilters: (filers: AdminCommentFilters) => void;
  setQuery: (query: string) => void;
  filters: AdminCommentFilters;
  query?: string;
  comments: Comment[];
  canLoadMore?: boolean;
  loading?: boolean;
  loaded?: boolean;
}

interface AdminCommentFilters {
  before_id?: number;
  user_id?: string;
  ref_id?: string;
  query?: string;
}

/**
 * This is hard coded into the backend. We just know that the api will always return pages of 10 comments.
 */
const PAGE_COUNT = 10;

export function useAdminComments(initialFilters?: AdminCommentFilters): UseAdminComments {
  const canLoadMore = React.useRef(false);
  const [query, setQuery] = React.useState('');
  const [filters, setFilters] = React.useState<AdminCommentFilters>({
    before_id: initialFilters?.before_id || 0,
    user_id: initialFilters?.user_id,
    ref_id: initialFilters?.ref_id,
    query: initialFilters?.query,
  });

  const loadQuery = useMethod(fetchComments, {
    autoRun: [filters],
    map(next, current) {
      canLoadMore.current = next.result.length >= PAGE_COUNT ? true : false;
      if (!next.args[0].before_id) {
        return next;
      }
      return {
        result: [...current?.result || [], ...next.result],
        args: next.args
      };
    },
  });

  const updateCommentInList = (id: number, changes: Partial<Comment>) => {
    const results = loadQuery.getLastResult();
    const commentIndex = results.findIndex(c => c.id === id);

    if(commentIndex === -1) return;
    const newList = [...results];
    newList[commentIndex] = {
      ...newList[commentIndex],
      ...changes,
    }
    loadQuery.setResult(newList);
  }

  const search = React.useMemo(() => {
    const db = debounce(([query]) => {
      const  q = typeof query === 'string' && query?.trim() ? query.trim() : undefined;
      setFilters(f => ({...f, query: q , before_id: 0}))
    }, 250);
    return (query: string) => {
      db(query);
      setQuery(query);
    }
  }, [])

  const del = (comment: Comment) => {
    updateCommentInList(comment.id, {
      body: '(Comment removed)',
      can_edit: false,
    })
    deleteComment(comment.ref_id, comment.id)
    .catch(err => {
      updateCommentInList(comment.id, {
        error:  'Failed to delete: ' + (err.data?.message || 'Unknown error'),
      });
    });
  }

  const edit = (comment: Comment, body: string) => {
    updateCommentInList(comment.id, {
      body: body,
    })
    editComment(comment.ref_id, comment.id, body)
    .catch(err => {
      updateCommentInList(comment.id, {
        error:  'Failed to update: ' + (err.data?.message || 'Unknown error'),
      });
    });
  }

  return {
    comments: loadQuery.result || [],
    loadMore: () => {
      const lastFilters = loadQuery.lastArgs?.[0] || { before_id: 0 };
      const results = loadQuery.getLastResult();
      if (!results.length) {
        loadQuery.run({...lastFilters, before_id: 0});
        return;
      }
      const lastId = results[results.length - 1].id;
      loadQuery.run({...lastFilters, before_id: lastId})
    },
    setFilters,
    setQuery: search,
    query: query,
    filters,
    canLoadMore: canLoadMore.current,
    loading: loadQuery.loading,
    loaded: loadQuery.count > 0,
    delete: del,
    edit,
  }
}

async function fetchComments(filters: AdminCommentFilters): Promise<Comment[]> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  const comments = await JsonFetch<Comment[]>({
    method: 'GET',
    url: `${baseUrl}/admin/comments?${toQueryParams(filters)}`,
  });

  return comments;
}

async function deleteComment(refId: string, commentId: number): Promise<void> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  await JsonFetch<Comment[]>({
    method: 'DELETE',
    url: `${baseUrl}/articles/${refId}/comments/${commentId}`,
  });
}

async function editComment(refId: string, commentId: number, body: string): Promise<void> {
  const baseUrl = getConfig().mxmAuxApiUrl;

   await JsonFetch<Comment[]>({
    method: 'PATCH',
    url: `${baseUrl}/articles/${refId}/comments/${commentId}`,
    data: {
      body
    }
  });
}

function toQueryParams(params: {[key: string]: any}) {
  return Object.keys(params)
  .filter(key => params[key] !== null && params[key] !== undefined)
  .map(key => `${key}=${encodeURIComponent(params[key])}`)
  .join('&');
}